import { appClient } from '@/lib/appwrite/appClient';
import { APP_COLLECTIONS, AppRuleGroup } from '@polybank/shared';
import { ID, Query } from 'appwrite';
import { logger } from '@/utils/logger';
import { appConfig } from '@/appConfig';

export class AppwriteRuleGroupService {
  async getGroups(): Promise<AppRuleGroup[]> {
    try {
      const response = await appClient.tables.typed.listRows(
        APP_COLLECTIONS.RULE_GROUPS,
        [Query.orderAsc('order'), Query.limit(100)]
      );
      return response.rows;
    } catch (error) {
      logger.error('Failed to get rule groups:', error);
      throw error;
    }
  }

  async createGroup(name: string, order: number): Promise<AppRuleGroup> {
    try {
      return await appClient.tables.typed.createRow(
        APP_COLLECTIONS.RULE_GROUPS,
        ID.unique(),
        { name, order }
      );
    } catch (error) {
      logger.error('Failed to create rule group:', error);
      throw error;
    }
  }

  async updateGroup(
    id: string,
    updates: Partial<{ name: string; order: number }>
  ): Promise<void> {
    try {
      await appClient.tables.typed.updateRow(
        APP_COLLECTIONS.RULE_GROUPS,
        id,
        updates
      );
    } catch (error) {
      logger.error('Failed to update rule group:', error);
      throw error;
    }
  }

  async deleteGroup(id: string): Promise<void> {
    try {
      await appClient.tables.typed.deleteRow(APP_COLLECTIONS.RULE_GROUPS, id);
    } catch (error) {
      logger.error('Failed to delete rule group:', error);
      throw error;
    }
  }

  async batchUpdateGroupOrder(
    updates: { $id: string; order: number }[]
  ): Promise<void> {
    const tx = await appClient.tables.createTransaction();

    const operations = updates.map((u) => ({
      action: 'update' as const,
      databaseId: appConfig.APPWRITE_DATABASE_ID!,
      tableId: APP_COLLECTIONS.RULE_GROUPS,
      rowId: u.$id,
      data: { order: u.order },
    }));

    if (operations.length > 0) {
      await appClient.tables.createOperations({
        transactionId: tx.$id,
        operations,
      });
    }
    await appClient.tables.updateTransaction({
      transactionId: tx.$id,
      commit: true,
      rollback: false,
    });
  }
}

export const appwriteRuleGroupService = new AppwriteRuleGroupService();
